import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, RouterStateSnapshot } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { OrganizationsStoreService } from '@shure/cloud/shared/services/organizations-store-service';
import { OrganizationsApiService } from '@shure/cloud/shure-associate/organizations/data-access';
import { PrismSnackbarService } from '@shure/prism-angular-components';

import { PermissionsService, ShPermissionsAggregate } from '../services/permissions.service';

export enum Apps {
	AssociatePortal = 'AssociatePortal'
}

@Injectable({ providedIn: 'root' })
export class PermissionsGuard {
	public permissions!: string | ShPermissionsAggregate;
	public appName!: string;

	constructor(
		private snackBarService: PrismSnackbarService,
		private permissionService: PermissionsService,
		private apiOrganizationsService: OrganizationsApiService,
		public organizationStoreService: OrganizationsStoreService,
		private translocoService: TranslocoService
	) {}

	public canActivate(route: ActivatedRouteSnapshot, _state: RouterStateSnapshot): Observable<boolean> {
		// eslint-disable-next-line dot-notation
		this.permissions = route.data['permissions'];
		// eslint-disable-next-line dot-notation
		this.appName = route.data['appName'];
		return this.doCheck();
	}

	private doCheck(): Observable<boolean> {
		const appName = this.appName === Apps.AssociatePortal ? 'adminportal' : 'organizationalportal';

		// Fetch the permissions for the app
		const appPermissions$ = this.permissionService.getPermissions$().pipe(catchError((error) => of(error)));

		// Fetch the version information for the app
		const versions$ = this.apiOrganizationsService
			.getVersion$Response({ appName: appName })
			.pipe(catchError((error) => of(error)));

		return forkJoin({
			permission: appPermissions$,
			version: versions$
		}).pipe(
			map((data) => {
				this.organizationStoreService.setVersion(data.version?.body?.body?.version);
				if (this.permissions) {
					return this.testPermissions();
				}
				return true;
			})
		);
	}

	/**
	 * Tests the permissions against the user's permissions.
	 * If the user does not have the required permissions, the navigation is blocked
	 * @returns {boolean} - Returns true if the user has the required permissions, false otherwise.
	 */
	private testPermissions(): boolean {
		const result = this.permissionService.testPermissions(this.permissions);
		if (!result) {
			this.snackBarService.open({
				message: this.translocoService.translate('access-error-message'),
				actionText: this.translocoService.translate('common.snackbar.ok')
			});
		}
		return result;
	}
}
